import React from 'react';
import { graphql } from 'gatsby';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import Img from 'gatsby-image';
import { CareersPageQuery } from '../../../graphql-types';

import Layout from '../../components/layout';
import SEO from '../../components/seo';
import PhotoCarousel from '../../components/locations/PhotoCarousel';

import { BREAKPOINTS, COLORS } from '../../ui/variables';
import Button from '../../ui/BaseButton';
import Heading1 from '../../ui/Heading1';
import Heading3 from '../../ui/Heading3';
import ParagraphExLarge from '../../ui/ParagraphExLarge';
import ParagraphLarge from '../../ui/ParagraphLarge';
import CareersNavigation from '../../components/careers/navigation';
import CareerTestimonials from '../../components/careers/testimonials';
import OpenPositions from '../../components/careers/open-positions';
import { TitleMedium, Uppercase } from '../../ui/TypographyStyles';

const Hero = styled.div`
  position: relative;
  margin-bottom: 40px;

  @media ${BREAKPOINTS.MOBILE} {
    padding-top: 233px;
    padding-bottom: 233px;
  }
`;

const HeroContent = styled.div`
  position: absolute;
  z-index: 2;
  color: ${COLORS.WHITE};
  max-width: 810px;
  width: 100%;
  text-align: center;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  padding-left: 20px;
  padding-right: 20px;
`;

const HeroTitle = styled(Heading1)`
  color: ${COLORS.WHITE};
`;

const HeroCTA = styled(Button)`
  padding: 18px 32px;

  @media ${BREAKPOINTS.MOBILE} {
    padding: 14px 24px;
  }
`;

const BasePad = styled.div`
  width: 100%;
  max-width: 1026px;
  margin: 140px auto 160px auto;
`;

export const TwoUp = styled.div`
  width: 100%;
  max-width: 1026px;
  margin: auto;
  margin-bottom: 120px;

  display: flex;
  justify-content: space-between;
  align-items: center;

  @media ${BREAKPOINTS.MOBILE} {
    flex-direction: column;
  }
`;

export const TwoUpChild = styled.div`
  width: 45%;
  flex-grow: 0;

  @media ${BREAKPOINTS.TABLET} {
    padding: 0 20px;
  }

  @media ${BREAKPOINTS.MOBILE} {
    width: 100%;
  }
`;

export const pageQuery = graphql`
  query CareersPage {
    heroImage: file(relativePath: { eq: "pages/careers/hero.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1440) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    insidePhoto: file(relativePath: { eq: "pages/careers/inside.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1440) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    groupPhoto: file(relativePath: { eq: "pages/careers/group-photo.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1440) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    feature1: file(
      relativePath: { eq: "pages/careers/career-carousel-1.jpg" }
    ) {
      childImageSharp {
        fixed(height: 517, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    feature2: file(
      relativePath: { eq: "pages/careers/career-carousel-2.jpg" }
    ) {
      childImageSharp {
        fixed(height: 517, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    feature3: file(
      relativePath: { eq: "pages/careers/career-carousel-3.jpg" }
    ) {
      childImageSharp {
        fixed(height: 517, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    feature4: file(
      relativePath: { eq: "pages/careers/career-carousel-4.jpg" }
    ) {
      childImageSharp {
        fixed(height: 517, quality: 100) {
          ...GatsbyImageSharpFixed_withWebp
        }
      }
    }
    founders: file(relativePath: { eq: "pages/careers/founders.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 486) {
          ...GatsbyImageSharpFluid_withWebp
          ...GatsbyImageSharpFluidLimitPresentationSize
        }
      }
    }
    investorLogos: file(
      relativePath: { eq: "pages/careers/investor-logos.png" }
    ) {
      childImageSharp {
        fluid(maxWidth: 486) {
          ...GatsbyImageSharpFluid_withWebp
          ...GatsbyImageSharpFluidLimitPresentationSize
        }
      }
    }
    valueDriven: file(relativePath: { eq: "pages/careers/value-driven.png" }) {
      childImageSharp {
        fluid(maxWidth: 486) {
          ...GatsbyImageSharpFluid_withWebp
          ...GatsbyImageSharpFluidLimitPresentationSize
        }
      }
    }
  }
`;

interface Props {
  data: CareersPageQuery;
}

const CareersPage = ({ data }: Props) => {
  return (
    <Layout>
      <SEO
        title='Veterinarian, LVT Nurse &amp; HQ Careers'
        description='Help us reimagine veterinary care. See jobs for veterinarians, nurses, LVTs, nurse assistants and corporate roles at Small Door across New York.'
      />
      <CareersNavigation />

      <Hero>
        <HeroContent>
          <Uppercase style={{ marginBottom: 24 }}>
            Careers at Small Door
          </Uppercase>
          <HeroTitle>Reimagine the veterinary industry with us</HeroTitle>
          <HeroCTA
            color={COLORS.ORANGE}
            type='external'
            href='https://boards.greenhouse.io/smalldoor'
          >
            See our open positions
          </HeroCTA>
        </HeroContent>
        <Img
          css={css`
            @media ${BREAKPOINTS.MOBILE} {
              display: none;
            }
          `}
          fluid={data.heroImage.childImageSharp.fluid}
        />
        <Img
          css={css`
            display: none;
            @media ${BREAKPOINTS.MOBILE} {
              display: block;
            }
          `}
          style={{ position: 'initial' }}
          imgStyle={{
            filter: 'brightness(45%)',
          }}
          fluid={data.groupPhoto.childImageSharp.fluid}
        />
      </Hero>

      <BasePad>
        <TitleMedium
          css={css`
            @media ${BREAKPOINTS.TABLET} {
              padding: 0 20px;
            }
          `}
        >
          Small Door is membership-based veterinary care designed with human
          standards that is better for pets, pet parents, and our medical staff
          alike.{' '}
        </TitleMedium>
        <ParagraphExLarge
          css={css`
            margin: 60px 0 140px 0;
            @media ${BREAKPOINTS.TABLET} {
              padding: 0 20px;
            }
          `}
        >
          We designed a reimagined veterinary experience via a membership that
          includes exceptional care, 24/7 telemedicine, and transparent pricing
          - delivered with modern hospitality in beautiful spaces designed by
          animal experts to be stress-free. We opened our flagship location in
          Manhattan's West Village in 2020 as proof of concept, and have
          continued our expansion into Boston and Washington,&nbsp;DC.
        </ParagraphExLarge>
      </BasePad>

      <TwoUp>
        <TwoUpChild>
          <Heading3>Experienced</Heading3>
          <ParagraphLarge>
            This is the fifth company that Small Door's founders have been
            involved in building. In building Small Door, they partnered with
            the people responsible for beloved consumer experiences like Warby
            Parker and Sweetgreen, in addition to healthcare innovators such as
            One Medical Group, CityMD and Flatiron Health.{' '}
          </ParagraphLarge>
        </TwoUpChild>
        <TwoUpChild
          css={css`
            @media ${BREAKPOINTS.TABLET} {
              margin-bottom: 60px;
              order: -1;
            }
          `}
        >
          <Img fluid={data.founders.childImageSharp.fluid} />
        </TwoUpChild>
      </TwoUp>

      <TwoUp>
        <TwoUpChild>
          <Img fluid={data.investorLogos.childImageSharp.fluid} />
        </TwoUpChild>
        <TwoUpChild>
          <Heading3>Well Funded</Heading3>
          <ParagraphLarge>
            Small Door has raised over $60 million from Lerer Hippeau, Primary
            Ventures, Brand Foundry, Toba Capital, Pentland Group, and
            Triplepoint Capital. The broader management team brings experience
            from Veterinary Emergency Group, Casper, One Medical Group,
            Soulcycle, Harry’s, Cityblock Health, Warby Parker, Sweetgreen, Gilt
            Groupe, and Lightspeed.
          </ParagraphLarge>
        </TwoUpChild>
      </TwoUp>

      <TwoUp>
        <TwoUpChild>
          <Heading3>Value Driven</Heading3>
          <ParagraphLarge>
            Our core values of high standards, collaboration, transparency,
            respect and ownership sit at the heart of Small Door and set the
            ground rules for all that we do. As we seek to grow our brand and
            membership, we constantly challenge ourselves to keep them at the
            forefront.
          </ParagraphLarge>
        </TwoUpChild>
        <TwoUpChild
          css={css`
            @media ${BREAKPOINTS.TABLET} {
              margin-bottom: 60px;
              order: -1;
            }
          `}
        >
          <Img fluid={data.valueDriven.childImageSharp.fluid} />
        </TwoUpChild>
      </TwoUp>

      <div>
        <PhotoCarousel
          maxHeight='410px'
          images={[
            { fixed: data.feature3.childImageSharp.fixed },
            { fixed: data.feature2.childImageSharp.fixed },
            { fixed: data.feature1.childImageSharp.fixed },
            { fixed: data.feature4.childImageSharp.fixed },
          ]}
        />
      </div>

      <CareerTestimonials />
      <OpenPositions
        positions={[
          {
            name: 'Veterinarians',
            linkTo: '/careers/veterinarians',
            linkCopy: 'Learn more',
          },
          {
            name: 'Nurses',
            linkTo: '/careers/nurses',
            linkCopy: 'Learn more',
          },
          {
            name: 'Headquarters',
            linkTo: '/careers/headquarters',
            linkCopy: 'Learn more',
          },
        ]}
      />
    </Layout>
  );
};

export default CareersPage;
